import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import interLatinWoff2 from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader, SiteFooter } from "@/components/site-chrome";
import { CookieConsent } from "@/components/cookie-consent";
import { GoogleAnalytics } from "@/components/google-analytics";
import logoAsset from "@/assets/pixiweb-logo.png.asset.json";

function NotFoundComponent() {
  return (
    <main className="container-pixi flex flex-1 items-center justify-center py-24 text-center">
      <div className="max-w-md">
        <p className="eyebrow">Error 404</p>
        <h1 className="mt-3 font-display text-6xl font-bold text-pixi-gradient">Seite nicht gefunden</h1>
        <p className="mt-4 text-slate">Diese URL existiert nicht. Vielleicht hilft ein Sprung zurück.</p>
        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 rounded-xl bg-navy px-6 py-3 text-sm font-semibold text-white transition hover:bg-electric hover:shadow-pixi"
        >
          Zur Startseite
        </Link>
      </div>
    </main>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  useEffect(() => { reportLovableError(error, { boundary: "tanstack_root_error_component" }); }, [error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-ice px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl font-bold text-navy">Da ist etwas schiefgelaufen</h1>
        <p className="mt-2 text-sm text-slate">Bitte versuche es erneut.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-xl bg-electric px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
          >
            Erneut versuchen
          </button>
          <a href="/" className="rounded-xl border border-mist bg-white px-5 py-2.5 text-sm font-semibold text-navy hover:bg-ice">Startseite</a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "O0agoCZwC48FFIvax6ozOHonyfBKfhRw3A0hTMTjxKQ" },
      { title: "PIXIWEB — Pixelgenaues Webdesign für KMU" },
      { name: "description", content: "Pixelgenaues Webdesign, Entwicklung & SEO für KMU — moderne, schnelle, DSGVO-konforme Websites aus einer Hand." },
      { name: "author", content: "PIXIWEB" },
      { name: "theme-color", content: "#2D3651" },
      { name: "format-detection", content: "telephone=no" },
      { property: "og:site_name", content: "PIXIWEB" },
      { property: "og:locale", content: "de_DE" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "PIXIWEB — Pixelgenaues Webdesign für KMU" },
      { property: "og:description", content: "Pixelgenaues Webdesign, Entwicklung & SEO für KMU — moderne, schnelle, DSGVO-konforme Websites aus einer Hand." },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "PIXIWEB — Pixelgenaues Webdesign für KMU" },
      { name: "twitter:description", content: "Pixelgenaues Webdesign, Entwicklung & SEO für KMU — moderne, schnelle, DSGVO-konforme Websites aus einer Hand." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/c3fe0f1b-0997-4156-bc3b-911f165689fb/id-preview-f7679e4c--6c7b8a64-810c-46f1-94e4-9afcd8d8f9b5.lovable.app-1782326546214.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/c3fe0f1b-0997-4156-bc3b-911f165689fb/id-preview-f7679e4c--6c7b8a64-810c-46f1-94e4-9afcd8d8f9b5.lovable.app-1782326546214.png" },
    ],
    links: [
      { rel: "preload", as: "font", type: "font/woff2", href: interLatinWoff2, crossOrigin: "anonymous" },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: logoAsset.url },
      { rel: "apple-touch-icon", href: logoAsset.url },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
              "@id": "https://pixiweb.de/#organization",
              name: "PIXIWEB",
              alternateName: "Pixiweb",
              url: "https://pixiweb.de",
              logo: { "@type": "ImageObject", url: "https://pixiweb.de" + logoAsset.url },
              image: "https://pixiweb.de" + logoAsset.url,
              email: "kontakt@pixiweb.de",
              telephone: "+4917640417218",
              priceRange: "€€",
              founder: { "@type": "Person", name: "Daniel Peters" },
              address: {
                "@type": "PostalAddress",
                streetAddress: "Vor dem Esch 7",
                postalCode: "32609",
                addressLocality: "Hüllhorst",
                addressRegion: "NRW",
                addressCountry: "DE",
              },
              areaServed: { "@type": "Country", name: "Deutschland" },
              description:
                "Webdesign, Entwicklung und SEO für kleine und mittelständische Unternehmen.",
              sameAs: [],
            },
            {
              "@type": "WebSite",
              "@id": "https://pixiweb.de/#website",
              url: "https://pixiweb.de",
              name: "PIXIWEB",
              inLanguage: "de-DE",
              publisher: { "@id": "https://pixiweb.de/#organization" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="de">
      <head><HeadContent /></head>
      <body>{children}<Scripts /></body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const pathname = router.state.location.pathname;
  const isHome = pathname === "/";
  const isBare = pathname.startsWith("/admin") || pathname.startsWith("/auth");
  const showChrome = !isBare;
  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col">
        {showChrome && <SiteHeader />}
        <main className="flex-1" style={showChrome && !isHome ? { paddingTop: 96 } : undefined}>
          <Outlet />
        </main>
        {showChrome && <SiteFooter />}
        {!isBare && <CookieConsent />}
        {!isBare && <GoogleAnalytics />}
      </div>

    </QueryClientProvider>
  );
}

